import React, { useEffect, useRef, useState } from "react";
import { RUN_STATE_LABEL } from "../../_config/quarterlyOkrConfig";
import "./_qokr-quarter-select.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The coach scorecard's quarter picker — the CEO app's `QokrQuarterSelect`
// (same look, same stylesheet, byte-identical scss) on the coach's data: the
// client's AI quarters from the dashboard (§9.1). No rhythm gear: changing a
// company's quarterly rhythm is the CEO's call.
//
//   • trigger: "Quarter" · the open quarter (+ "Current") · chevron;
//   • menu: next first, then current, then earlier (by dates), each with
//     Next / Current / Ended, where its run is, and "Not completed".
//
// `quarters`: dashboard rows `{ quarter_company_id, label, start_date,
// end_date, run_state, not_completed }`; `onChange` gets the picked id.
// ═══════════════════════════════════════════════════════════════════════════

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dayOf = (date) => (date ? String(date).slice(0, 10) : null);

// The BE's quarter flags win when a row carries them (2026-11-02, same
// precedence as the CEO app: Archived → Ended → Grace period → Current);
// otherwise the dashboard row's dates decide, as before. Ask the BE to send
// `is_current_quarter` / `is_in_grace` / `grace_days_left` /
// `is_quarter_ended` / `is_archived` on the dashboard rows too — dates alone
// show a quarter in its grace period as "Ended".
const graceLabel = (quarter) => {
  const days = Number(quarter.grace_days_left) || 0;
  return days
    ? `Grace period · ${days} ${days === 1 ? "day" : "days"} left`
    : "Grace period";
};

const stateOf = (quarter, today) => {
  if (quarter.is_archived) return { label: "Archived", tone: "archived" };
  if (quarter.is_quarter_ended) return { label: "Ended", tone: "ended" };
  if (quarter.is_in_grace) return { label: graceLabel(quarter), tone: "grace" };
  if (quarter.is_current_quarter) return { label: "Current", tone: "current" };
  const start = dayOf(quarter.start_date);
  const end = dayOf(quarter.end_date);
  if (start && end && start <= today && today <= end)
    return { label: "Current", tone: "current" };
  if (start && start > today) return { label: "Next", tone: "next" };
  if (end && end < today) return { label: "Ended", tone: "ended" };
  return null;
};

const RANK = { next: 0, current: 1, grace: 2, ended: 3, archived: 4 };

const ordered = (quarters, today) =>
  quarters
    .map((q, index) => ({ q, state: stateOf(q, today), index }))
    .sort((a, b) => {
      const ra = a.state ? RANK[a.state.tone] : 5;
      const rb = b.state ? RANK[b.state.tone] : 5;
      if (ra !== rb) return ra - rb;
      // Next: soonest first; ended: latest first.
      const sa = String(dayOf(a.q.start_date) || "");
      const sb = String(dayOf(b.q.start_date) || "");
      if (a.state?.tone === "next") return sa.localeCompare(sb);
      if (a.state?.tone === "ended") return sb.localeCompare(sa);
      return a.index - b.index;
    });

const CalendarIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M3 10h18M8 3v4M16 3v4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const QokrQuarterSelect = ({ quarters = [], selectedId, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  const today = todayIso();
  const list = ordered(quarters, today);
  const selected = list.find(
    ({ q }) => String(q.quarter_company_id) === String(selectedId),
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (quarter) => {
    setOpen(false);
    onChange?.(quarter.quarter_company_id);
  };

  return (
    <div className="qokr-qsel" ref={wrapRef}>
      <div className={`qokr-qsel__control${open ? " is-open" : ""}`}>
        <button
          type="button"
          ref={triggerRef}
          className="qokr-qsel__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          <span className="qokr-qsel__icon">
            <CalendarIcon />
          </span>
          <span className="qokr-qsel__text">
            <span className="qokr-qsel__eyebrow">Quarter</span>
            <span className="qokr-qsel__value">
              {selected?.q.label || "Select a quarter"}
              {selected?.state ? (
                <span className={`qokr-qsel__chip is-${selected.state.tone}`}>
                  {selected.state.tone === "grace"
                    ? "Grace period"
                    : selected.state.label}
                </span>
              ) : null}
            </span>
          </span>
          <i
            className={`fa-solid fa-chevron-down qokr-qsel__chev${open ? " is-open" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>

      {open ? (
        <div className="qokr-qsel__menu" role="listbox" aria-label="Quarters">
          <div className="qokr-qsel__menu-head">Quarters</div>
          {list.map(({ q: quarter, state }) => {
            const isSelected =
              String(quarter.quarter_company_id) === String(selectedId);
            const runLabel = quarter.not_completed
              ? null
              : RUN_STATE_LABEL[quarter.run_state] || null;
            return (
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                key={quarter.quarter_company_id}
                className={`qokr-qsel__option${isSelected ? " is-selected" : ""}`}
                onClick={() => pick(quarter)}
              >
                <span
                  className={`qokr-qsel__dot${state ? ` is-${state.tone}` : ""}`}
                />
                <span className="qokr-qsel__option-text">
                  <span className="qokr-qsel__option-name">
                    {quarter.label}
                  </span>
                  <span className="qokr-qsel__tags">
                    {state ? (
                      <span className={`qokr-qsel__chip is-${state.tone}`}>
                        {state.label}
                      </span>
                    ) : null}
                    {runLabel ? (
                      <span className="qokr-qsel__chip is-ai">{runLabel}</span>
                    ) : null}
                    {quarter.not_completed ? (
                      <span className="qokr-qsel__chip is-muted">
                        Not completed
                      </span>
                    ) : null}
                  </span>
                </span>
                {isSelected ? (
                  <i
                    className="fa-solid fa-check qokr-qsel__check"
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          })}
          {!list.length ? (
            <div className="qokr-qsel__empty">No quarters yet.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default QokrQuarterSelect;
